import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Img from 'react-image';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import hash from 'object-hash';
import { FlexBox, Typography, Card, Grid, MuiBox } from '@kis-technologies/kis-ui';
import WillCallLogo from 'assets/will_call_icon.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { Paginator } from 'components/Paginator';
import { ProcessingOverlay } from 'components/ProcessingOverlay';
import { useActions } from 'hooks/useActions';
import {
  selectors as configSelectors,
  Configuration,
  PromoCodeSelection,
  PromoCodeCustomOption,
  LocalizedStrRecord
} from 'services/configuration';
import { selectors as authSelectors, actions as authActions } from 'services/auth';
import { selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import { selectors as mediaSelectors, MediaResponse } from 'services/media';
import { selectors as ticketingSelectors, actions as ticketingActions } from 'services/ticketing';
import { useStyles } from './styled';

const getOrderedOptions = (options: PromoCodeCustomOption[], tileOrder?: string[]): PromoCodeCustomOption[] => {
  let orderedOptions: PromoCodeCustomOption[] = [];
  const enabledOptions = [...options];
  if (tileOrder && tileOrder.length) {
    tileOrder.forEach((tile) => {
      const tileOption = enabledOptions.find((item) => item.code === tile);
      if (tileOption) {
        orderedOptions = orderedOptions.concat(enabledOptions.splice(enabledOptions.indexOf(tileOption), 1));
      }
    });
  }
  orderedOptions = orderedOptions.concat(enabledOptions);

  return orderedOptions;
};

const PromoCodeSelectionView = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { applyPromoCode, resetPromoCode, resetSelectedPromoCode, setSelectedPromoCode, resetAdminPinAuthorized } =
    useActions({
      applyPromoCode: ticketingActions.applyPriceTypePromoCode,
      resetPromoCode: ticketingActions.resetPriceTypePromoCode,
      resetSelectedPromoCode: ticketingActions.resetSelectedPromoCode,
      setSelectedPromoCode: ticketingActions.setSelectedPromoCode,
      resetAdminPinAuthorized: authActions.resetAdminPinAuthorized
    });

  const adminPinAuthorized = useSelector(authSelectors.selectAdminPinAuthorized);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const promoCodeImages = useSelector(mediaSelectors.selectPromoCodeImages);
  const { loading, error, processed, selectedCode } = useSelector(ticketingSelectors.selectPriceTypePromoCode);
  const appliedCode = useSelector(ticketingSelectors.selectAppliedPriceTypePromo);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const tiersConfig = useSelector(ticketingSelectors.selectTiersConfig);

  const palette = get(configuration, 'configuration.globalSettings.palette');
  const promoCodeSelection: PromoCodeSelection = get(configuration, `${ticketingPath}.promoCode.selection`, {});
  const header: string = get(promoCodeSelection, `messages.header.${i18n.language}`, t('promoCode.selectionHeader'));
  const subheader: string = get(promoCodeSelection, `messages.subheader.${i18n.language}`, '');
  const customOptions: PromoCodeCustomOption[] = get(promoCodeSelection, 'customOptions', []);
  const manualEntryEnabled: boolean = get(promoCodeSelection, 'manualEntry.enabled', false);
  const manualEntryMessages: LocalizedStrRecord = get(promoCodeSelection, 'manualEntry.messages', {});
  const tileOrder: string[] = get(promoCodeSelection, 'tileOrder', []);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOption, setSelectedOption] = useState<PromoCodeCustomOption | null>(null);
  const [showInfoOverlay, setShowInfoOverlay] = useState<boolean>(false);
  const [showProcessingOverlay, setShowProcessingOverlay] = useState<boolean>(false);

  const classes = useStyles();

  const { clientId } = kiosk.venue;
  const previewUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/promo_code_images/`;
  const isTierSelected = Boolean(tiersConfig && tiersConfig.selected && tiersConfig.selected.tier);

  // make a list of options to render as tiles, using customOptions and manualEntry
  let promoOptions = [...customOptions];
  if (manualEntryEnabled) promoOptions.push({ code: 'manualEntry', messages: manualEntryMessages });
  promoOptions = getOrderedOptions(promoOptions, tileOrder);

  // ON PAGE LOAD
  useEffect(() => {
    // processed=true if a code was manually applied and returned here
    if (processed) handleCodeApplied();
    selectAppliedPromo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // APPLY PROMO CODE AFTER ADMIN APPROVAL
  useEffect(() => {
    if (adminPinAuthorized && selectedCode) {
      // Once PIN is validated, start applying the promo automatically
      const selectedPromoOption = promoOptions.find((po) => po.code === selectedCode);
      setSelectedOption(selectedPromoOption as PromoCodeCustomOption);
      setShowProcessingOverlay(true);
      resetAdminPinAuthorized();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPinAuthorized]);

  // SELECT THE TILE ASSOCIATED TO AN APPLIED PROMO CODE
  const selectAppliedPromo = () => {
    if (appliedCode) {
      const appliedPromoOption = promoOptions.find((po) => po.code === appliedCode);
      if (appliedPromoOption) setSelectedOption(appliedPromoOption);
    } else if (selectedOption) {
      setSelectedOption(null);
    }
  };

  // GET THE S3 FILE NAME FOR AN UPLOADED IMAGE
  const getPromoCodeImageUrl = (promoCode: string) => {
    const imgObj =
      promoCode &&
      promoCodeImages.find((item: MediaResponse) => {
        return item.name === hash(promoCode.replace(new RegExp('/', 'g'), ' '));
      });
    // image from media table
    if (imgObj && imgObj.s3Name) {
      return previewUrl.concat(imgObj.s3Name);
    }

    return '';
  };

  // SAVE SELECTED PROMO CODE AND NAVIGATE TO APPROVAL
  const onRequiresAttendantApproval = (promoCode: string) => {
    setSelectedPromoCode(promoCode);
    navigate('/ticketing/promo-code/approval');
  };

  // HIDE INFO OVERLAY AND START PROCESSING
  const handleSubmit = () => {
    if (selectedOption?.requiresAttendantApproval) {
      onRequiresAttendantApproval(selectedOption?.code as string);
    } else {
      setShowProcessingOverlay(true);
    }
    setShowInfoOverlay(false);
  };

  // HIDE INFO OVERLAY AND RESET SELECTION
  const handleCancel = () => {
    selectAppliedPromo();
    setShowInfoOverlay(false);
  };

  // HIDE OVERLAYS AND RESET SELECTION
  const handleTryAgain = () => {
    selectAppliedPromo();
    resetPromoCode();
    setShowProcessingOverlay(false);
  };

  // RESET STATUS AND NAV BACK
  const handleCodeApplied = () => {
    resetPromoCode();
    navigateToPreviousPage();
  };

  // NAV TO INPUT, SHOW INFO OVERLAY, GET ATTENDANT APPROVAL, OR START PROCESSING
  const handleTileClick = (promoOption: PromoCodeCustomOption) => {
    setSelectedOption(promoOption);
    if (promoOption.code === 'manualEntry') {
      navigate('/ticketing/price-type/promo', { state: { from: location.pathname } });
    } else if (promoOption.infoOverlay?.enabled) {
      setShowInfoOverlay(true);
    } else if (promoOption.requiresAttendantApproval) {
      onRequiresAttendantApproval(promoOption.code as string);
    } else {
      setShowProcessingOverlay(true);
    }
  };

  const onPrevPageClick = () => {
    if (selectedCode) resetSelectedPromoCode();
    navigateToPreviousPage();
  };

  const navigateToPreviousPage = () => {
    navigate(isTierSelected ? '/ticketing/selection' : '/ticketing/tiers');
  };

  return (
    <>
      <Grid className={classes.outerContainer}>
        <Grid container className={classes.innerContainer}>
          {/* HEADER/SUBHEADER */}
          <MuiBox className={classes.headerContainer}>
            <Typography variant="h2">{header}</Typography>
            <Typography fontSize={30} fontWeight={300}>
              {subheader}
            </Typography>
          </MuiBox>
          {/* PROMO OPTIONS AS TILES; 4 PER PAGE */}
          {promoOptions.length > 0 ? (
            <Paginator
              items={promoOptions}
              itemsPerPage={4}
              page={currentPage}
              onPageChange={(newPage) => setCurrentPage(newPage)}
            >
              {({ items }) => (
                <div className={classes.allItemsContainer}>
                  {items.map((promoOption, idx) => (
                    <MuiBox key={idx} className={classes.itemContainer}>
                      <Card
                        hasBorderRadius
                        raised
                        fullHeight
                        hasBorder={promoOption.code === selectedOption?.code}
                        borderColor={palette?.primary}
                      >
                        <Card.ActionArea onClick={() => handleTileClick(promoOption)}>
                          <Grid container className={classes.itemContentContainer}>
                            <Grid item className={classes.itemImageContainer}>
                              <Img
                                src={[getPromoCodeImageUrl(promoOption.code || ''), `${previewUrl}default.png`]}
                                className={classes.itemImage}
                                unloader={
                                  <img src={WillCallLogo} alt="Event Logo" className={classes.itemDefaultImage} />
                                }
                              />
                            </Grid>
                            <Grid item>
                              <MuiBox className={classes.itemHorizontalDivider}>
                                <Card.Divider />
                              </MuiBox>
                              <MuiBox className={classes.itemVerticalDivider} />
                            </Grid>
                            <Grid container item className={classes.itemTextContainer} direction="column">
                              <FlexBox direction="column" justify="center" align="start">
                                <Typography fontSize={28} fontWeight={400}>
                                  {get(promoOption, `messages.${i18n.language}`, '')}
                                </Typography>
                              </FlexBox>
                            </Grid>
                          </Grid>
                        </Card.ActionArea>
                      </Card>
                    </MuiBox>
                  ))}
                </div>
              )}
            </Paginator>
          ) : (
            <FlexBox height="600px" align="center">
              <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
            </FlexBox>
          )}
        </Grid>
      </Grid>

      {/* FOOTER */}
      <Footer onPrevClick={onPrevPageClick} />

      {/* PROCESSING OVERLAY WHEN APPLYING THE CODE */}
      {showProcessingOverlay && (
        <ProcessingOverlay
          processed={processed}
          processAction={() => setTimeout(() => applyPromoCode(selectedOption && selectedOption.code), 1000)}
          title={t('promoCode.promoLoadingText')}
          onAnimationComplete={handleCodeApplied}
          error={!!error}
        />
      )}

      {/* INFO OVERLAY FOR A CUSTOM OPTION, DISPLAYED WHEN SELECTED (IF CONFIGURED) */}
      {showInfoOverlay && (
        <ActionOverlay
          title={get(selectedOption, `infoOverlay.messages.header.${i18n.language}`)}
          description={
            <Typography fontSize={32} align="center">
              {get(selectedOption, `infoOverlay.messages.subheader.${i18n.language}`)}
            </Typography>
          }
          confirmText={get(selectedOption, `infoOverlay.messages.buttons.${i18n.language}[1]`, t('commonText.apply'))}
          cancelText={get(selectedOption, `infoOverlay.messages.buttons.${i18n.language}[0]`, t('commonText.cancel'))}
          onCancel={handleCancel}
          onConfirm={handleSubmit}
          style={{ zIndex: '2147483648' }}
        />
      )}

      {/* ERROR OVERLAY */}
      {error && !loading && (
        <ActionOverlay
          title={<Typography className={classes.errorOverlayTitle}>{t('promoCode.invalidPromotion')}</Typography>}
          description={
            <Typography className={classes.errorOverlayDescription}>{t('promoCode.selectionErrorText')}</Typography>
          }
          confirmText={t('commonText.tryAgain')}
          onConfirm={handleTryAgain}
        />
      )}
    </>
  );
};

export default PromoCodeSelectionView;
